import { useEffect, useState } from 'react';
import { fetchInvoices } from '../services/invoicesApi';

/**
 * The first 4 digits of the *next* invoice number, derived from the most recent
 * invoice raised. Invoice numbers are `YY` (year) + a 4-digit counter reset each
 * year (e.g. `261111`); we increment the latest to get the next number, then
 * take its first 4 digits (`261111` → next `261112` → `2611`) as a pre-printed
 * prefix on the A4 ticket sheet — the technician writes the last two. Using the
 * *next* number keeps the prefix correct across hundred-boundaries (latest
 * `261199` → next `261200` → `2612`). `prefix` is null until resolved (and stays
 * null on error / when no invoices exist), so callers fall back to their default.
 */
interface UseLatestInvoicePrefixResult {
  prefix: string | null;
  loading: boolean;
}

/**
 * First 4 digits of the invoice number *after* `latest` (e.g. `261199` → `2612`).
 * Returns null when the latest number is missing or non-numeric.
 */
export function nextInvoicePrefix(latest: string | undefined | null): string | null {
  const trimmed = latest?.trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) return null;
  const next = String(Number(trimmed) + 1).padStart(trimmed.length, '0');
  return next.length >= 4 ? next.slice(0, 4) : null;
}

export function useLatestInvoicePrefix(): UseLatestInvoicePrefixResult {
  const [prefix, setPrefix] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetchInvoices({ page: 1, pageSize: 1, sort: 'number', order: 'desc', signal: controller.signal })
      .then((res) => {
        setPrefix(nextInvoicePrefix(res.items[0]?.number));
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setPrefix(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  return { prefix, loading };
}
